import {
  Organization,
  OrganizationContext,
  useClient,
} from '@foxtrotplatform/platform-micro-site-core';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useAuth } from 'react-oidc-context';

import { getOrganizationTenant } from '../api/organization';
import { getEmailDomainOrg, getUserOrgs } from '../api/user';
import { SELECTED_USER_ORG_ID } from '../constants';
import {
  DeveloperOrganization,
  DeveloperOrganizationContextProps,
  Tenant,
} from './types';

const initialOrganizationState: DeveloperOrganizationContextProps = {
  setCurrentOrg: (org: Organization) => ({ org }),
  isOrganizationsReady: false,
  requiresOrgOnboarding: true,
  refreshOrganizations: () => ({}),
  refreshOrganizationTenant: () => ({}),
};

export interface OrganizationProviderProps {
  children?: React.ReactNode;
}

export const OrganizationProvider: FunctionComponent<
  OrganizationProviderProps
> = (props: OrganizationProviderProps): JSX.Element => {
  const { user } = useAuth();
  const { didInstallHeaders } = useClient();
  const [userOrgs, setUserOrgs] = useState<DeveloperOrganization[]>();
  const [currentOrg, setCurrentOrg] = useState<DeveloperOrganization>();
  const [currentTenant, setCurrentTenant] = useState<Tenant>();
  const [userEmailDomainOrg, setUserEmailDomainOrg] =
    useState<DeveloperOrganization>();
  const [requiresOrgOnboarding, setRequiresOrgOnboarding] =
    useState<boolean>(false);
  const [domainOrgChecked, setDomainOrgChecked] = useState<boolean>();
  const [userOrgsLoaded, setUserOrgsLoaded] = useState<boolean>();
  const [organizationProps, setOrganizationProps] =
    useState<DeveloperOrganizationContextProps>(initialOrganizationState);

  useEffect(() => {
    if (didInstallHeaders) {
      getOrganizations();
    }
  }, [didInstallHeaders]);

  useEffect(() => {
    if (didInstallHeaders) {
      const getUserEmailDomainOrg = async () => {
        const emailDomainOrg = await getEmailDomainOrg();

        setUserEmailDomainOrg(emailDomainOrg);
        setDomainOrgChecked(true);
      };

      getUserEmailDomainOrg();
    }
  }, [didInstallHeaders]);

  useEffect(() => {
    if (userOrgs) {
      const requiresOrgOnboarding = userOrgs?.length === 0;
      setRequiresOrgOnboarding(requiresOrgOnboarding);
    }
  }, [userOrgs]);

  const refreshOrganizationTenant = useCallback(async () => {
    if (!currentOrg?.id) {
      return;
    }
    const tenant = await getOrganizationTenant(currentOrg.id);
    setCurrentTenant(tenant);
  }, [setCurrentTenant, currentOrg?.id]);

  useEffect(() => {
    if (currentOrg?.id) {
      refreshOrganizationTenant();
    }
  }, [refreshOrganizationTenant, currentOrg?.id]);

  const getOrganizations = async () => {
    const currentOrgs = await getUserOrgs();

    if (currentOrgs) {
      const currentOrgId = localStorage.getItem(SELECTED_USER_ORG_ID);
      const currentOrg = currentOrgs.find(({ id }) => id === currentOrgId);
      setUserOrgs(currentOrgs);
      setCurrentOrg(currentOrg || currentOrgs[0]);
    }
    setUserOrgsLoaded(true);
  };

  const refreshOrganizations = useCallback(async () => {
    getOrganizations();
  }, []);

  useEffect(() => {
    if (user) {
      const organizationProps: DeveloperOrganizationContextProps = {
        currentOrg,
        currentTenant,
        userEmailDomainOrg,
        requiresOrgOnboarding,
        isOrganizationsReady: (domainOrgChecked && userOrgsLoaded) || false,
        organizations: userOrgs,
        refreshOrganizations,
        refreshOrganizationTenant,
        setCurrentOrg: (org) => {
          setCurrentOrg({ ...(org as DeveloperOrganization) });
        },
      };
      setOrganizationProps(organizationProps);
    } else {
      setOrganizationProps(initialOrganizationState);
    }
    if (currentOrg) {
      localStorage.setItem(SELECTED_USER_ORG_ID, currentOrg.id);
    }
  }, [
    currentOrg,
    currentTenant,
    userEmailDomainOrg,
    requiresOrgOnboarding,
    userOrgs,
    user,
  ]);

  return (
    <OrganizationContext.Provider value={organizationProps}>
      {props.children}
    </OrganizationContext.Provider>
  );
};
